import { SmgElement } from '../core/smg-element';
import { each, html, when, type SafeHtml } from '../core/safe-html';
import { ApiError } from '../models/common';
import type { ScriptInfo } from '../models/device';
import { session } from '../services/auth-service';
import { confirmDialog } from '../services/confirm-service';
import { devices } from '../services/device-service';
import { showToast } from '../services/toast-service';

const NEW_TEMPLATE = `#!/bin/sh
# smg-desc: Bu script ne yapar? (panelde açıklama olarak görünür)
# smg-args: <arg1> [arg2]   (argüman ipucu)
set -u
echo "merhaba $(hostname) — seri: \${SMG_SERIAL:-?}"
`;

/**
 * Script kütüphanesi: sunucudaki /opt/smg/scripts/*.sh — cihaz RAM'de çalıştırır
 * (smg-script; diske yazmaz). Görüntüleme herkese, düzenleme admin'e.
 */
export class ScriptsView extends SmgElement {
  private list: ScriptInfo[] = [];
  private loading = true;
  private error = '';
  private selected: string | null = null;
  private content = '';
  private newName = '';
  private isNew = false;
  private dirty = false;

  protected override onConnected(): void {
    this.wireActs();
    void this.load();
  }

  private canEdit(): boolean {
    return session.get().claims?.brands ?? false; // admin
  }

  private async load(select?: string): Promise<void> {
    this.loading = true;
    this.error = '';
    this.render();
    try {
      this.list = await devices.scripts();
      const target = select ?? this.selected ?? this.list[0]?.name ?? null;
      if (target) await this.open(target);
      else this.selected = null;
    } catch {
      this.error = 'Script listesi alınamadı.';
    } finally {
      this.loading = false;
      this.render();
    }
  }

  private async open(name: string): Promise<void> {
    this.selected = name;
    this.isNew = false;
    this.dirty = false;
    try {
      this.content = await devices.scriptBody(name);
    } catch {
      this.content = '';
    }
  }

  private wireActs(): void {
    this.onAct('click', {
      pick: (el) => void this.open(el.dataset['name'] ?? '').then(() => this.render()),
      newScript: () => {
        this.isNew = true;
        this.selected = null;
        this.newName = '';
        this.content = NEW_TEMPLATE;
        this.dirty = true;
        this.render();
      },
      save: () => void this.save(),
      remove: () => void this.removeScript(),
    });
    this.onAct('input', {
      content: (el) => {
        this.content = (el as HTMLTextAreaElement).value;
        this.dirty = true;
      },
      newName: (el) => (this.newName = (el as HTMLInputElement).value.trim().toLowerCase()),
    });
  }

  private async save(): Promise<void> {
    const name = this.isNew ? this.newName : this.selected;
    if (!name || !/^[a-z0-9][a-z0-9._-]{0,63}$/.test(name)) {
      showToast('Ad: küçük harf/rakam/._- (3-64)', 'warning');
      return;
    }
    try {
      await devices.saveScript(name, this.content);
      showToast(`"${name}" kaydedildi`);
      this.dirty = false;
      void this.load(name);
    } catch (e) {
      showToast(e instanceof ApiError && e.message ? e.message : 'Kaydedilemedi', 'warning');
    }
  }

  private async removeScript(): Promise<void> {
    if (!this.selected) return;
    const ok = await confirmDialog({
      title: 'Script silinsin mi?',
      message: `"${this.selected}" sunucudan kalıcı olarak silinecek. Cihazlarda kopyası yok (RAM'de çalışır).`,
      tone: 'danger',
      icon: 'ti ti-trash',
      okLabel: 'Sil',
    });
    if (!ok) return;
    try {
      await devices.deleteScript(this.selected);
      showToast('Silindi');
      this.selected = null;
      void this.load();
    } catch {
      showToast('Silinemedi', 'warning');
    }
  }

  protected override template(): SafeHtml {
    const editable = this.canEdit();
    return html`
      <div class="scr">
        <div style="flex:none;background:var(--vx-surface);border-bottom:1px solid var(--vx-border);padding:16px 16px 14px">
          <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
            <div>
              <div style="font:600 21px var(--vx-font-sans)">Script kütüphanesi</div>
              <div style="font:400 12.5px/1.4 var(--vx-font-sans);color:var(--vx-text-2);margin-top:2px">
                Cihazlarda RAM'de çalışır (diske yazılmaz). Cihaz detayından tek cihaza, listeden toplu gönderilir.
              </div>
            </div>
            ${when(editable, () => html`
              <button class="btn pri sm" style="margin-left:auto" data-act="newScript"><i class="ti ti-plus"></i>Yeni script</button>`)}
          </div>
        </div>
        ${this.bodyTemplate(editable)}
      </div>`;
  }

  private bodyTemplate(editable: boolean): SafeHtml {
    if (this.loading) {
      return html`<div class="body" style="display:flex;align-items:center;justify-content:center">
        <i class="ti ti-loader-2 spin" style="font-size:26px;color:var(--vx-primary)"></i></div>`;
    }
    if (this.error) {
      return html`<div class="body" style="display:flex;align-items:center;justify-content:center;padding:24px">
        <div style="text-align:center;color:var(--vx-text-2)"><i class="ti ti-plug-connected-x" style="font-size:30px;color:var(--vx-text-3)"></i>
        <div style="font:500 13.5px var(--vx-font-sans);margin-top:8px">${this.error}</div></div></div>`;
    }
    return html`
      <div class="body" style="padding:16px;display:flex;flex-wrap:wrap;gap:16px;align-content:flex-start;overflow-x:hidden">
        <div class="card" style="flex:1 1 280px;padding:8px;max-width:420px">
          <p class="sect" style="padding:8px 8px 4px;margin:0">Scriptler (${this.list.length})</p>
          ${each(this.list, (s) => {
            const active = this.selected === s.name && !this.isNew;
            return html`
              <button class="mrow" style="background:${active ? 'var(--vx-brand-50)' : 'transparent'};align-items:flex-start" data-act="pick" data-name="${s.name}">
                <span style="width:32px;height:32px;border-radius:9px;background:${active ? 'var(--vx-brand-100)' : 'var(--vx-chip-bg)'};display:flex;align-items:center;justify-content:center;flex:none">
                  <i class="ti ti-terminal-2" style="font-size:16px;color:${active ? 'var(--vx-primary)' : 'var(--vx-text-3)'}"></i>
                </span>
                <span style="flex:1;min-width:0">
                  <span class="mono" style="display:block;font:600 13px var(--vx-font-mono);color:${active ? 'var(--vx-brand-700)' : 'var(--vx-text)'}">${s.name}</span>
                  <span style="display:block;font:400 11px/1.4 var(--vx-font-sans);color:var(--vx-text-3)">${s.description || '—'}</span>
                </span>
              </button>`;
          })}
          ${when(!this.list.length && !this.isNew, () => html`
            <div style="padding:14px;color:var(--vx-text-3);font:400 12.5px var(--vx-font-sans)">Henüz script yok.</div>`)}
        </div>

        ${when(this.selected || this.isNew, () => html`
          <div class="card" style="flex:2 1 460px;min-width:0;display:flex;flex-direction:column;gap:12px">
            <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
              ${when(this.isNew, () => html`
                <div style="flex:1 1 200px"><label class="lbl">Script adı</label>
                  <input class="inp mono" data-act="newName" placeholder="ornek-script" value="${this.newName}"></div>`)}
              ${when(!this.isNew, () => html`
                <div style="flex:1;min-width:0">
                  <div class="mono" style="font:600 15px var(--vx-font-mono)">${this.selected}.sh</div>
                  <div style="font:400 11px var(--vx-font-sans);color:var(--vx-text-3)">${this.list.find((s) => s.name === this.selected)?.args ? `argümanlar: ${this.list.find((s) => s.name === this.selected)!.args}` : 'argüman yok'}</div>
                </div>`)}
              ${when(editable && !this.isNew, () => html`
                <button class="btn dngo sm" data-act="remove"><i class="ti ti-trash"></i>Sil</button>`)}
              ${when(editable, () => html`
                <button class="btn pri sm" data-act="save" ${this.dirty ? '' : 'disabled'}><i class="ti ti-device-floppy"></i>Kaydet</button>`)}
            </div>
            <textarea class="inp mono" data-act="content" style="height:440px;line-height:1.5;font-size:12.5px;padding:12px" ${editable ? '' : 'readonly'} spellcheck="false">${this.content}</textarea>
            <div style="font:400 11px/1.5 var(--vx-font-sans);color:var(--vx-text-3)">
              Başlıktaki <span class="mono"># smg-desc:</span> açıklama, <span class="mono"># smg-args:</span> argüman ipucu olur.
              Cihazda <span class="mono">sh -s -- &lt;args&gt;</span> ile root olarak çalışır; <span class="mono">$SMG_SERIAL</span> hazırdır.
              Çıktı ve exit kodu komut geçmişine yazılır (16 KB'a kadar).
            </div>
          </div>`)}
      </div>`;
  }
}

customElements.define('smg-scripts-view', ScriptsView);
